import { metIntegration } from '@metservicedev/platforms-services-interface/src/services/metIntegration/metIntegration';
import { InfoPopover, IssueTime } from '@mono/metui';
import { toHash } from '@mono/to-hash';
import type { RunwayRainGaugeSettings } from '@mono/types/src/runway-widgets';
import type { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { usePlatStageContext } from '@mono/use-plat-stage/src/use-plat-stage';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';
import useSize from '@react-hook/size';
// biome-ignore lint/style/useImportType: <explanation>
import React, { useEffect, useRef, useState } from 'react';

const toolbarStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
} as React.CSSProperties;

interface runwayResponse {
    'met:station-id': string;
    'met:product-name': '1minuteobservation';
    'met:domain': 'aviation';
    'met:issue-time': string;
    'met:valid-at': string;
    'av:airport-location': string;
    'met:data': {
        airTemperature01HourMax: string;
        airTemperature01HourMin: string;
        airTemperature01MinuteAverage: string;
        cloud1Amount30MinuteAverage: string;
        cloud1Height30MinuteAverage: string;
        cloud2Amount30MinuteAverage: string;
        cloud2Height30MinuteAverage: string;
        cloud3Amount30MinuteAverage: string;
        cloud3Height30MinuteAverage: string;
        dewTemperature01MinuteAverage: string;
        equipmentTemperature01MinuteAverage: string;
        observationTimestamp: string;
        powerVoltage01MinuteAverage: string;
        presentWeatherNumeric115MinuteAverage: string;
        presentWeatherString115MinuteAverage: string;
        pressureMSL01HourMax: string;
        pressureMSL01HourMin: string;
        pressureMSL01MinuteAverage: string;
        pressureQFE01HourMax: string;
        pressureQFE01HourMin: string;
        pressureQFE01MinuteAverage: string;
        pressureQNH01HourMax: string;
        pressureQNH01HourMin: string;
        pressureQNH01MinuteAverage: string;
        pressureSensor01HourChg: string;
        pressureSensor01HourMax: string;
        pressureSensor01HourMin: string;
        pressureSensor01MinuteAverage: string;
        rainfall01HourAccumulation: string;
        rainfall01HourMax: string;
        rainfall01MinuteAccumulation: string;
        rainfall10MinuteAccumulation: string;
        rainfall10MinuteMax: string;
        relativeHumidity01HourMax: string;
        relativeHumidity01HourMin: string;
        relativeHumidity01MinuteAverage: string;
        stationAlphaId: string;
        stationNumericId: string;
        stationStatusType: string;
        visibility01MinuteAverage: string;
        visibility10MinuteAverage: string;
        windCCW01HourMax: string;
        windCCW01MinuteMax: string;
        windCCW10MinuteMax: string;
        windCW01HourMax: string;
        windCW01MinuteMax: string;
        windCW10MinuteMax: string;
        windDirection01HourAverage: string;
        windDirection01MinuteAverage: string;
        windDirection10MinuteAverage: string;
        windGust01HourDirection: string;
        windGust01HourMax: string;
        windGust01HourTime: string;
        windGust01MinuteMax: string;
        windGust10MinuteMax: string;
        windLull01MinuteMin: string;
        windLull10MinuteMin: string;
        windRun01HourAccumulation: string;
        windSpeed01HourAverage: string;
        windSpeed01MinuteAverage: string;
        windSpeed10MinuteAverage: string;
        windCCWM01HourMax: string;
        windCCWM01MinuteMax: string;
        windCCWM10MinuteMax: string;
        windCWM01HourMax: string;
        windCWM01MinuteMax: string;
        windCWM10MinuteMax: string;
        windDirectionM01HourAverage: string;
        windDirectionM01MinuteAverage: string;
        windDirectionM10MinuteAverage: string;
        windGustM01HourDirection: string;
    };
}

const getGradient = (colour?: string) => {
    switch (colour) {
        case '#FA6161':
            return { light: '#FA6161', dark: '#E93F3F' };
        case '#FF981B':
            return { light: '#FF981B', dark: '#CE7100' };
        case '#74A30F':
            return { light: '#74A30F', dark: '#577E00' };
        default:
            return { light: '#0060D8', dark: '#2E7CDD' };
    }
};

const RainGauge = ({
    accumulation,
    threshold,
    ticks,
    title,
    svgHeight,
    svgWidth,
    theme,
    mode,
}: {
    accumulation?: number;
    threshold: { value: number; colour: string };
    ticks: string[];
    title: string;
    svgHeight: number;
    svgWidth: number;
    theme: { text: string };
    mode?: 'cozy' | 'compact';
}) => {
    if (svgWidth <= 0 || svgHeight <= 0) return <></>;
    const acc = accumulation !== undefined ? accumulation : 0;
    const accString = accumulation !== undefined ? accumulation.toFixed(2) : 'n/a';
    const rainGaugeHeight = svgHeight - 16 - 8 - 30 - 6 - 12;
    const rainGaugeWidth = svgWidth - 35 - 7 - 4;
    const unitHeight = rainGaugeHeight / Number.parseFloat(ticks[ticks.length - 1]);

    const colour = acc >= threshold.value ? threshold.colour : '#0060D8';
    const { light, dark } = getGradient(colour);

    const gradientTitle = `gradient-${title.replace(/(\)|\(| |)(?!=\w)/g, '')}`;

    return (
        <svg height={svgHeight} width={svgWidth}>
            <title>Rain Gauge SVG</title>
            <linearGradient id={`${gradientTitle}`} x1='0%' y1='0%' x2='0%' y2='100%'>
                <stop offset='0%' stopColor={light} />
                <stop offset='100%' stopColor={dark} />
            </linearGradient>
            <g>
                <rect
                    x={1}
                    y={rainGaugeHeight - acc * unitHeight + 10}
                    width={rainGaugeWidth}
                    height={acc * unitHeight}
                    fill={`url(#${gradientTitle})`}
                    rx={2}
                />

                {ticks.map((value, index) => {
                    const num = Number.parseFloat(value);
                    return (
                        <g key={`tick-${num}`}>
                            <text
                                x={rainGaugeWidth + 11}
                                y={rainGaugeHeight - num * unitHeight + 11}
                                dominantBaseline={'middle'}
                                fill={theme.text}
                                fontSize={mode === 'cozy' ? 20 : 14}
                            >
                                {value}
                            </text>
                            {index === 0 || index === ticks.length - 1 ? null : (
                                <line
                                    x1={1}
                                    x2={rainGaugeWidth + 7}
                                    y1={rainGaugeHeight - num * unitHeight + 10}
                                    y2={rainGaugeHeight - num * unitHeight + 10}
                                    stroke={theme.text}
                                    opacity={0.2}
                                />
                            )}
                        </g>
                    );
                })}
                <rect x={1} y={10} width={rainGaugeWidth} height={rainGaugeHeight} stroke={theme.text} strokeWidth={1} rx={4} fill={'none'} />
                {/* Please in future use a table here */}
                <text
                    x={1}
                    y={mode === 'cozy' ? svgHeight - 38 + 6 : svgHeight - 38}
                    style={{ fontWeight: 700, fontSize: mode === 'cozy' ? 20 : 12 }}
                    fill={theme.text}
                >
                    {title}
                </text>
                <line x1={0} x2={svgWidth} y1={svgHeight - 24} y2={svgHeight - 24} stroke={'#CDCDCD'} shapeRendering={'crispEdges'} />
                <text x={0} y={svgHeight - 2} fill={theme.text} style={{ fontWeight: 400, fontSize: mode === 'cozy' ? 20 : 14 }}>
                    {accString}
                </text>
            </g>
        </svg>
    );
};

export const RunwayRainGauge = ({ settings, theme: themeName }: WeatherWidgetRenderProps<RunwayRainGaugeSettings>) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [width, height] = useSize(chartRef);
    const { stage } = usePlatStageContext();

    const { title, aerodrome, timezone, weatherStations, thresholds, defaultColour } = settings;
    const [tableData, setTableData] = useState<{
        threeMinAvg: number;
        tenMinAvg: number;
        stationId: string;
        issueTime: string;
    }>();

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const svgWidth = width / 2;
    const svgHeight = height - 32 - 10;

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              issueTime: '#ffffff',
          }
        : {
              text: '#172344',
              issueTime: '#595959',
          };

    // biome-ignore lint/correctness/useExhaustiveDependencies: toHash(weatherStations) captures changes to the length and nature of the weather stations
    useEffect(() => {
        const metjwt = localStorage.getItem('metjwt');
        const { metserviceApi } = metIntegration;

        let timeoutHook = 0;

        const patientQuery = async () => {
            try {
                const headers: { authorization: string } = metjwt
                    ? {
                          authorization: `Bearer ${metjwt}`,
                      }
                    : { authorization: '' };

                for (let i = 0; i < weatherStations.length; i++) {
                    const response = await fetch(
                        `${metserviceApi[stage]}/aviation/observations/airport/${aerodrome}/weatherStation/${weatherStations[i]}/last/4/minutes`,
                        {
                            headers,
                        }
                    );
                    const data = await response.json();
                    if (data.response.length < 3) continue;
                    const sensorData = data.response as runwayResponse[];

                    const threeMinData: number = sensorData.slice(0, 3).reduce((acc, curr) => {
                        const oneMinValue = Number.parseFloat(curr['met:data'].rainfall01MinuteAccumulation || '0');
                        if (!oneMinValue) return acc;
                        return acc + oneMinValue;
                    }, 0);

                    const stationId = sensorData[0]['met:station-id'];

                    setTableData({
                        threeMinAvg: threeMinData,
                        tenMinAvg: Number.parseFloat(sensorData[0]['met:data'].rainfall10MinuteAccumulation),
                        stationId,
                        issueTime: sensorData[0]['met:issue-time'],
                    });
                    return;
                }
            } catch (e) {
                console.error(e);
            } finally {
                timeoutHook = setTimeout(patientQuery, 60 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            clearTimeout(timeoutHook);
        };
    }, [toHash(weatherStations), aerodrome, stage]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                font: '400 12px/18px Open Sans',
                color: theme.text,
                overflow: 'hidden',
            }}
            ref={chartRef}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={toolbarStyle}>
                    <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700 }}>{title}</div>
                </div>
                <div style={{ display: 'flex', columnGap: 4 }}>
                    {tableData?.issueTime && (
                        <IssueTime
                            issueTime={tableData.issueTime}
                            targetTimezone={targetTimezone}
                            style={toolbarStyle}
                            colour={theme.issueTime}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    <div style={toolbarStyle}>
                        <InfoPopover>
                            <div style={{ padding: '5px 16px', color: '#595959' }}>
                                <div style={{ display: 'flex', paddingBottom: 8 }}>
                                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <title>Rainfall 3 min thresholds</title>
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 12.7909 16 8.79086 12 3C8 8.79086 6 12.7909 6 15ZM12 19.5C9.51472 19.5 7.5 17.4853 7.5 15C7.5 13.4139 8.99987 10.2331 12 5.68003C15.0001 10.2331 16.5 13.4139 16.5 15C16.5 17.4853 14.4853 19.5 12 19.5Z'
                                            fill='#909AB5'
                                        />
                                        <path opacity='0.3' d='M12 21C15.3137 21 18 18.3137 18 15H6C6 18.3137 8.68629 21 12 21Z' fill='#909AB5' />
                                        <rect x='16.5' y='15' width='0.75' height='2.25' transform='rotate(90 16.5 15)' fill='#909AB5' />
                                        <rect x='21' y='15' width='0.75' height='2.25' transform='rotate(90 21 15)' fill='#909AB5' />
                                        <rect x='5.25' y='15' width='0.75' height='2.25' transform='rotate(90 5.25 15)' fill='#909AB5' />
                                        <rect x='12.75' y='15' width='0.75' height='2.25' transform='rotate(90 12.75 15)' fill='#909AB5' />
                                        <rect x='9' y='15' width='0.75' height='2.25' transform='rotate(90 9 15)' fill='#909AB5' />
                                    </svg>
                                    <div style={{ paddingLeft: 8, color: '#595959' }}>
                                        <b>RAINFALL 3 MIN THRESHOLDS</b>
                                        <br />
                                        <b>Dry:</b> 0.00mm
                                        <br />
                                        <b style={{ color: defaultColour }}>Light:</b> 0.01mm - {thresholds.threeMinute.value}mm
                                        <br />
                                        <b style={{ color: thresholds.threeMinute.colour }}>Heavy:</b> Greater than or equal to {thresholds.threeMinute.value}mm
                                        <br />
                                        <b>No Data:</b> Sensor not working
                                    </div>
                                </div>
                                <div style={{ display: 'flex', paddingBottom: 8 }}>
                                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <title>Rainfall 10 min thresholds</title>
                                        <path
                                            fillRule='evenodd'
                                            clipRule='evenodd'
                                            d='M6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 12.7909 16 8.79086 12 3C8 8.79086 6 12.7909 6 15ZM12 19.5C9.51472 19.5 7.5 17.4853 7.5 15C7.5 13.4139 8.99987 10.2331 12 5.68004C15.0001 10.2331 16.5 13.4139 16.5 15C16.5 17.4853 14.4853 19.5 12 19.5Z'
                                            fill='#909AB5'
                                        />
                                        <path
                                            opacity='0.3'
                                            d='M11.7942 20.2533C20.4078 20.2533 15.5442 10.6569 14.7942 9H8.79422C8.04422 10.6569 3.1806 20.2533 11.7942 20.2533Z'
                                            fill='#909AB5'
                                        />
                                        <rect x='16.5' y='9' width='0.75' height='2.25' transform='rotate(90 16.5 9)' fill='#909AB5' />
                                        <rect x='20.25' y='9' width='0.75' height='2.25' transform='rotate(90 20.25 9)' fill='#909AB5' />
                                        <rect x='5.25' y='9' width='0.75' height='2.25' transform='rotate(90 5.25 9)' fill='#909AB5' />
                                        <rect x='12.75' y='9' width='0.75' height='2.25' transform='rotate(90 12.75 9)' fill='#909AB5' />
                                        <rect x='9' y='9' width='0.75' height='2.25' transform='rotate(90 9 9)' fill='#909AB5' />
                                    </svg>
                                    <div style={{ paddingLeft: 8, color: '#595959' }}>
                                        <b>RAINFALL 10 MIN THRESHOLDS</b>
                                        <br />
                                        <b>Dry:</b> 0.00mm
                                        <br />
                                        <b style={{ color: defaultColour }}>Light:</b> 0.01mm - {thresholds.tenMinute.value}mm
                                        <br />
                                        <b style={{ color: thresholds.tenMinute.colour }}>Heavy:</b> Greater than or equal to {thresholds.tenMinute.value}mm
                                        <br />
                                        <b>No Data:</b> Sensor not working
                                    </div>
                                </div>
                                Observations are taken from the automatic weather stations located at the airport. Data expected to update every 1-2 minutes.
                            </div>
                        </InfoPopover>
                    </div>
                </div>
            </div>
            <hr
                style={{
                    width: '100%',
                    border: 'none',
                    borderBottom: '1px solid #cdcdcd',
                }}
            />
            <div style={{ display: 'flex', flex: 1 }}>
                <div style={{ flex: 1 }}>
                    <RainGauge
                        accumulation={tableData?.threeMinAvg}
                        threshold={thresholds.threeMinute}
                        ticks={['0', '.25', '.5', '.75', '1+']}
                        title={`3 MIN ACC (mm) ${tableData?.stationId ? `(${tableData?.stationId})` : ''}`}
                        svgHeight={svgHeight}
                        svgWidth={svgWidth}
                        theme={theme}
                        mode={settings.mode}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <RainGauge
                        accumulation={tableData?.tenMinAvg}
                        threshold={thresholds.tenMinute}
                        ticks={['0', '.5', '1', '1.5', '2+']}
                        title={`10 MIN ACC (mm) ${tableData?.stationId ? `(${tableData?.stationId})` : ''}`}
                        svgHeight={svgHeight}
                        svgWidth={svgWidth}
                        theme={theme}
                        mode={settings.mode}
                    />
                </div>
            </div>
        </div>
    );
};
